import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/intel/Layout";
import { NetworkGraph } from "@/components/intel/NetworkGraph";
import { SeverityBadge } from "@/components/intel/SeverityBadge";
import { OnboardingTour, type TourStep } from "@/components/intel/OnboardingTour";
import { SubjectProfile } from "@/components/intel/SubjectProfile";
import { exportScanDossier, exportCaseDossier } from "@/lib/intel/pdf";
import { runIntelScan, type BackendScanResult } from "@/lib/intel/scan.functions";
import { askAnalyst } from "@/lib/intel/feeds.functions";
import { getPaddleEnvironment } from "@/lib/paddle";
import { ScanAiChat } from "@/components/intel/ScanAiChat";
import { detectTargetType } from "@/lib/intel/detect";
import { useWorkspace } from "@/lib/workspace/useWorkspace";
import { TAB_LABELS, type DashboardTab } from "@/lib/workspace/workspaces";
import { BusinessIntelPanel } from "@/components/intel/BusinessIntelPanel";
import { BreachExposureBanner } from "@/components/intel/BreachExposureBanner";
import { BreachRecordsList, extractBreachesFromRun, extractHibpStatus } from "@/components/intel/BreachRecordsList";
import { SourceReliabilityPanel } from "@/components/intel/SourceReliabilityPanel";
import { SourceMeshPanel } from "@/components/intel/SourceMeshPanel";
import { DeepResults, type DeepResultsData } from "@/components/intel/DeepResults";
import { EvidenceSections } from "@/components/intel/EvidenceSections";

import { ContradictionPanel } from "@/components/intel/ContradictionPanel";
import { FootprintScorePanel } from "@/components/intel/FootprintScorePanel";
import { ConfidencePanel } from "@/components/intel/ConfidencePanel";
import { DisambiguationPanel } from "@/components/intel/DisambiguationPanel";
import { assessIdentityAmbiguity } from "@/lib/intel/disambiguation.functions";
import type { AmbiguityReport, IdentityCandidate } from "@/lib/intel/disambiguation-types";
import { estimateAmbiguityPrior } from "@/lib/intel/ambiguity";
import type { Entity } from "@/lib/intel/mock";
import { useInvestigations } from "@/lib/intel/store";
import {
  Search, Play, Download, Share2, Filter, Brain, Sparkles, AlertTriangle,
  ShieldCheck, Activity, Globe, Wallet, Mail, Building2, Cpu, MapPin,
  ArrowUpRight, ChevronRight, Send, Bot, SlidersHorizontal, LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";

function initials(s: string): string {
  const t = s.trim();
  if (!t) return "—";
  const parts = t.split(/[\s._@-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "—").toUpperCase() + (parts[1]?.[0]?.toUpperCase() ?? "");
}

export const Route = createFileRoute("/dashboard")({
  validateSearch: (s: Record<string, unknown>): { checkout?: string; q?: string } => ({
    ...(typeof s.checkout === "string" ? { checkout: s.checkout } : {}),
    ...(typeof s.q === "string" && s.q.trim() ? { q: s.q.slice(0, 200) } : {}),
  }),
  head: () => ({
    meta: [
      { title: "Investigate · CARIO INTEL" },
      { name: "description", content: "Live investigation command surface." },
    ],
  }),
  component: Dashboard,
});

function Dashboard() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  useEffect(() => {
    if (search.checkout === "success") {
      toast.success("Subscription activated — welcome aboard.");
      navigate({ to: "/dashboard", search: { checkout: "" }, replace: true });
    }
  }, [search.checkout, navigate]);
  const [target, setTarget] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [running, setRunning] = useState(false);
  const [targetTypeOverride, setTargetTypeOverride] = useState<"auto" | "person" | "organization">("auto");
  const emptyRefine = { dob: "", location: "", address: "", email: "", phone: "", employer: "", aliases: "", notes: "" };
  const [refine, setRefine] = useState(emptyRefine);
  const [showRefine, setShowRefine] = useState(false);
  const refineActive = Object.values(refine).some((v) => v.trim().length > 0);
  const [scanResult, setScanResult] = useState<BackendScanResult | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const { workspace } = useWorkspace();
  const [tab, setTab] = useState<DashboardTab>("profile");
  const [typeTouched, setTypeTouched] = useState(false);
  const [selected, setSelected] = useState<Entity | null>(null);
  const runScan = useServerFn(runIntelScan);
  const checkAmbiguity = useServerFn(assessIdentityAmbiguity);
  const [ambiguityReport, setAmbiguityReport] = useState<AmbiguityReport | null>(null);
  const [checkingAmbiguity, setCheckingAmbiguity] = useState(false);
  const [clarifyPrompt, setClarifyPrompt] = useState<
    { target: string; score: number; reasons: string[]; clarifiers: string[] } | null
  >(null);

  // Clean, detail-first read-out assembled from the deep mesh + Grok dossier.
  const deepResults = useMemo<DeepResultsData | null>(() => {
    if (!scanResult) return null;
    const raw = scanResult.raw as any;
    const pattern = raw?.deepPattern;
    const dossier = raw?.deepDossier;
    const analysis = raw?.grokAnalysis;
    if (!pattern && !dossier) return null;
    const coverage = (scanResult.raw.collectors ?? []).find((c) => c.source === "Endpoint family coverage")?.data as
      | { round1?: Array<{ attempted: number; executed: number; hits: number }>; round2?: Array<{ attempted: number; executed: number; hits: number }> }
      | undefined;
    const rounds = [...(coverage?.round1 ?? []), ...(coverage?.round2 ?? [])];
    const contactRecord = (scanResult.raw.collectors ?? []).find((c) => c.source === "Contact graph")?.data as
      | DeepResultsData["contacts"]
      | undefined;
    const sum = (k: "attempted" | "executed" | "hits") => rounds.reduce((n, r) => n + (r[k] ?? 0), 0);
    return {
      target: scanResult.target,
      headline: dossier?.headline || analysis?.identity_summary || scanResult.target,
      bottomLine: dossier?.bottom_line || analysis?.narrative || "",
      sections: Array.isArray(dossier?.sections) ? dossier.sections : [],
      clusters: Array.isArray(pattern?.clusters) ? pattern.clusters : [],
      signals: Array.isArray(pattern?.signals) ? pattern.signals : [],
      breadth: pattern?.breadth ?? 0,
      intensity: pattern?.intensity ?? 0,
      probesPlanned: rounds.length ? sum("attempted") : undefined,
      probesExecuted: rounds.length ? sum("executed") : undefined,
      probeHits: rounds.length ? sum("hits") : undefined,
      contacts: contactRecord ?? null,
      social: Array.isArray(raw?.socialLane) ? raw.socialLane : [],
      unresolved: Array.isArray(dossier?.unresolved) ? dossier.unresolved : [],
      nextSteps: Array.isArray(dossier?.next_steps)
        ? dossier.next_steps
        : Array.isArray(analysis?.pivots)
          ? analysis.pivots.map((p: any) => p?.target).filter(Boolean)
          : [],
    };
  }, [scanResult]);

  // Derive the expensive result views once per scan instead of on every
  // keystroke in the search box.
  const collectors = useMemo<any[]>(() => ((scanResult?.raw as any)?.collectors ?? []), [scanResult]);
  const findingsList = useMemo(() => scanResult?.findings ?? [], [scanResult]);
  const breachView = useMemo(
    () => (scanResult ? { breaches: extractBreachesFromRun(scanResult), hibp: extractHibpStatus(scanResult) } : null),
    [scanResult],
  );



  // Keep the console aligned with the user's workspace: default target type and
  // an available tab. Manual choices win.
  useEffect(() => {
    if (!typeTouched && workspace.defaultTargetType !== "auto") {
      setTargetTypeOverride(workspace.defaultTargetType);
    }
  }, [workspace.defaultTargetType, typeTouched]);
  useEffect(() => {
    setTab((t) => (workspace.tabs.includes(t) ? t : (workspace.tabs[0] ?? "profile")));
  }, [workspace.tabs]);

  // Pivot from other modules (e.g. photo match candidates) via /dashboard?q=...
  useEffect(() => {
    if (prefilled || !search.q) return;
    setTarget(search.q);
    setPrefilled(true);
  }, [search.q, prefilled]);

  const applyPlaybook = (id: string) => {
    const pb = workspace.playbooks.find((p) => p.id === id);
    if (!pb) return;
    if (pb.targetTypeOverride) { setTargetTypeOverride(pb.targetTypeOverride); setTypeTouched(true); }
    if (pb.notes) { setRefine((r) => ({ ...r, notes: pb.notes! })); setShowRefine(true); }
    if (pb.mode === "business" && workspace.tabs.includes("business")) { setTab("business"); return; }
    if (target.trim()) void startScan();
  };
  const { state, createCase, updateCase, addEvidence, addNote, logScan, logExport, setLastScan } = useInvestigations();
  const activeCaseId = state.cases[0]?.id ?? null;

  // Rehydrate from the store so navigation back to /dashboard keeps the last scan visible
  useEffect(() => {
    if (scanResult || !state.lastScan) return;
    if (search.q) return;
    const ls = state.lastScan;
    setTarget(ls.target);
    setScanResult({
      id: ls.id,
      target: ls.target,
      targetType: ls.targetType as BackendScanResult["targetType"],
      status: "completed",
      investigationStatus: "SUCCESS",
      scoreStatus: ls.riskScore === null ? "INSUFFICIENT_EVIDENCE" : "CALCULATED",
      scoringModelVersion: "cached",
      usableEvidenceCount: 0,
      riskScore: ls.riskScore,
      trustScore: ls.trustScore ?? (ls.riskScore === null ? null : 100 - ls.riskScore),
      scoreBreakdown: (ls.scoreBreakdown as BackendScanResult["scoreBreakdown"]) ?? {
        base: 0,
        riskScore: ls.riskScore ?? 0,
        trustScore: ls.trustScore ?? (ls.riskScore === null ? 0 : 100 - ls.riskScore),
        categories: [],
      },
      confidence: ls.confidence,
      // A cached headline number cannot be decomposed after the fact. Show the
      // stored figure but never invent per-dimension scores to go with it.
      confidenceBreakdown: {
        status: "INSUFFICIENT_EVIDENCE",
        modelVersion: "cached",
        usableEvidenceCount: 0,
        identity: null,
        source: null,
        evidence: null,
        match: null,
        overall: ls.confidence,
        independentSources: 0,
        totalSources: 0,
        syndicationRatio: 0,
        coverage: { selected: 0, successful: 0, failed: 0, ratio: 0 },
        factors: [],
        caveats: ["Restored from local session cache — re-run the investigation for a full confidence audit."],
      },

      sourceCount: ls.sourceCount,
      findings: ls.findings,
      entities: ls.entities,
      timeline: ls.timeline,
      raw: { collectors: [], legalScope: "public-osint-only", generatedAt: ls.completedAt },
      createdAt: ls.completedAt,
      completedAt: ls.completedAt,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.lastScan]);

  const shareScan = async () => {
    if (!scanResult) { alert("Run a scan first to share results."); return; }
    const text = `CARIO INTEL · ${scanResult.target}\nRun ${scanResult.id}\nRisk ${scanResult.riskScore}/100 · Confidence ${scanResult.confidence}%\n${scanResult.sourceCount} collectors · ${scanResult.findings.length} findings`;
    try {
      await navigator.clipboard.writeText(text);
      alert("Scan summary copied to clipboard.");
    } catch {
      alert(text);
    }
  };

  const cleanRefine = (r: typeof emptyRefine) =>
    Object.fromEntries(
      Object.entries(r).map(([k, v]) => [k, v.trim()]).filter(([, v]) => (v as string).length > 0),
    ) as Record<string, string>;

  type ScanOptions = {
    /** Skip the pre-scan identity separation pass (already answered). */
    skipDisambiguation?: boolean;
    /** Analyst chose to proceed without adding narrowing details. */
    skipClarification?: boolean;
    ambiguity?: { score: number; candidateCount: number; selectedCandidate?: string };
    refineOverride?: typeof emptyRefine;
  };

  const startScan = async (opts: ScanOptions = {}) => {
    const normalizedTarget = target.trim();
    if (!normalizedTarget || running) return;
    const activeRefine = opts.refineOverride ?? refine;

    // P0 — separate identities BEFORE burning a full investigation on a name
    // that could belong to several different people.
    if (!opts.skipDisambiguation) {
      const detectedType = targetTypeOverride !== "auto" ? targetTypeOverride : detectTargetType(normalizedTarget);
      const prior = estimateAmbiguityPrior({ target: normalizedTarget, targetType: detectedType, refine: activeRefine });

      // A very common name or generic handle would blend several people into one
      // dossier. Ask for narrowing details BEFORE spending the investigation.
      if (prior.requiresClarification && !opts.skipClarification) {
        setScanError(null);
        setShowRefine(true);
        setClarifyPrompt({
          target: normalizedTarget,
          score: prior.score,
          reasons: prior.reasons,
          clarifiers: prior.suggestedClarifiers,
        });
        return;
      }
      setClarifyPrompt(null);

      if (prior.shouldEnumerate) {
        setCheckingAmbiguity(true);
        setScanError(null);
        try {
          // Never let the pre-scan identity check hang the UI: it is an assist,
          // so it gets a hard 25s budget and then the scan proceeds.
          const report = await Promise.race([
            checkAmbiguity({
              data: {
                target: normalizedTarget,
                targetType: detectedType,
                ...(Object.keys(cleanRefine(activeRefine)).length ? { refine: cleanRefine(activeRefine) } : {}),
              },
            }),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error("ambiguity check timed out")), 25_000),
            ),
          ]);
          if (report.needsClarification && report.candidates.length > 1) {
            setAmbiguityReport(report);
            setCheckingAmbiguity(false);
            return;
          }
          setAmbiguityReport(null);
          opts = {
            ...opts,
            ambiguity: { score: report.ambiguityScore, candidateCount: report.candidates.length },
          };
        } catch {
          // Disambiguation is an assist, never a blocker — fall through to the scan.
        } finally {
          setCheckingAmbiguity(false);
        }
      }
    }

    const caseId = !activeCaseId || state.cases[0]?.target !== normalizedTarget
      ? createCase({ title: `OSINT scan · ${normalizedTarget}`, target: normalizedTarget, tags: ["scan", "osint", "backend"] })
      : activeCaseId;
    logScan(caseId, "scan_started", `Backend job queued · target=${normalizedTarget}`);
    setScanError(null);
    setRunning(true);
    try {
      const cleanedRefine = cleanRefine(activeRefine);
      // Client-side stall guard: the run never spins indefinitely, even if the
      // network drops the response mid-flight.
      const result = await Promise.race([
        runScan({
          data: {
            target: normalizedTarget,
            workspace: workspace.id,
            ...(Object.keys(cleanedRefine).length ? { refine: cleanedRefine } : {}),
            ...(targetTypeOverride !== "auto" ? { targetTypeOverride } : {}),
            ...(opts.ambiguity ? { ambiguity: opts.ambiguity } : {}),
          },
        }),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("The investigation took too long and was stopped. Partial results are stored in your Vault — add a detail like location or employer and run it again.")),
            180_000,
          ),
        ),
      ]);

      setScanResult(result);
      setAmbiguityReport(null);
      setLastScan({
        id: result.id,
        target: result.target,
        targetType: result.targetType,
        riskScore: result.riskScore,
        trustScore: result.trustScore,
        scoreBreakdown: result.scoreBreakdown,
        confidence: result.confidence,
        sourceCount: result.sourceCount,
        entities: result.entities,
        findings: result.findings,
        timeline: result.timeline,
        completedAt: result.completedAt,
        caseId,
      });
      updateCase(caseId, {
        title: `OSINT scan · ${normalizedTarget}`,
        target: normalizedTarget,
        summary: `Backend scan ${result.id} completed with risk ${result.riskScore}/100, confidence ${result.confidence}%, and ${result.sourceCount} public collectors executed.`,
        tags: ["scan", "osint", "backend", result.targetType],
      });
      result.findings.slice(0, 5).forEach((finding) => addEvidence(caseId, {
        kind: "doc",
        label: finding.title,
        source: finding.source,
        content: `${finding.description}\n\nBackend run: ${result.id}\nTarget: ${result.target}\nSeverity: ${finding.severity}\nConfidence: ${finding.confidence}%`,
        tags: ["backend-scan", finding.category.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""), finding.severity],
      }));
      addNote(caseId, `Backend scan ${result.id} completed for ${result.target}. Risk ${result.riskScore}/100, confidence ${result.confidence}%, ${result.sourceCount} public collectors executed. Legal scope: ${result.raw.legalScope}`, true);
      logScan(caseId, "scan_completed", `Backend run ${result.id} · risk=${result.riskScore} conf=${result.confidence} · ${result.entities.length} entities · ${result.findings.length} findings`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Backend scan failed";
      setScanError(message);
      addNote(caseId, `Backend scan failed for ${normalizedTarget}: ${message}`);
    } finally {
      setRunning(false);
    }
  };
  const exportDashboardDossier = async () => {
    if (scanResult) {
      const ok = await exportScanDossier({
        result: scanResult,
        filename: `${scanResult.id}-scan-dossier.pdf`,
      });
      if (ok && activeCaseId) logExport(activeCaseId, `Scan dossier exported · run ${scanResult.id}`);
      else if (!ok) alert("PDF export failed — see console for details.");
      return;
    }
    const caseRow = state.cases[0];
    if (!caseRow) { alert("Run a scan or open a case first."); return; }
    const evidence = state.evidence.filter((e) => e.caseId === caseRow.id);
    const notes = state.notes.filter((n) => n.caseId === caseRow.id);
    const custody = state.custody.filter((c) => c.caseId === caseRow.id);
    const ok = await exportCaseDossier({
      caseRow, evidence, notes, custody,
      chain: { valid: true },
      filename: `${caseRow.id}-dashboard-dossier.pdf`,
    });
    if (ok) logExport(caseRow.id, `Dashboard case dossier exported`);
    else alert("PDF export failed — see console for details.");
  };



  // Null means "not calculable", which is NOT the same as zero — never coerce.
  const riskScore = scanResult?.riskScore ?? null;
  const confidence = scanResult?.confidence ?? null;
  const trustScore = scanResult?.trustScore ?? (riskScore === null ? null : 100 - riskScore);
  const primarySubject = scanResult?.entities[0];
  const liveTargetType = useMemo(() => (target.trim() ? detectTargetType(target) : null), [target]);
  const displayTargetType = (liveTargetType ?? scanResult?.targetType ?? null);

  return (
    <AppLayout>
      <div className="grid grid-cols-12 gap-3 p-3">
        {/* Command bar */}
        <div data-tour="command-bar" className="col-span-12 glass rounded-xl p-3"
          style={{ borderTop: `2px solid ${workspace.accent}` }}>
          <div className="mb-3 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest"
                style={{ color: workspace.accent, background: `color-mix(in oklab, ${workspace.accent} 12%, transparent)` }}>
                <LayoutDashboard className="h-3 w-3" /> {workspace.eyebrow}
              </span>
              <h1 className="mt-1.5 font-display text-lg sm:text-xl">Start an investigation</h1>
              <p className="text-xs text-muted-foreground">{workspace.headline}</p>
            </div>
            <button onClick={() => navigate({ to: "/settings" })}
              className="shrink-0 rounded-md border border-panel-border bg-panel/40 px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground">
              Change use case
            </button>
          </div>

          {/* Step 1 — what are you looking for */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">I'm searching for a</span>
              <div className="flex items-center gap-1 rounded-md border border-panel-border bg-panel/40 p-1 text-xs">
                {([["auto","Anything"],["person","Person"],["organization","Company"]] as const).map(([k, label]) => (
                  <button key={k} type="button" onClick={() => { setTargetTypeOverride(k); setTypeTouched(true); }}
                    className={`rounded px-2.5 py-1 ${targetTypeOverride === k ? "bg-cyan/20 text-cyan" : "text-muted-foreground hover:text-foreground"}`}
                    title={k === "auto" ? "Let CARIO detect the target type" : `Always treat the input as a ${k}`}>
                    {label}
                  </button>
                ))}
              </div>
              {displayTargetType && (
                <span className="rounded-full border border-panel-border px-2 py-0.5 text-[11px] text-muted-foreground">
                  Detected: <span className="text-cyan">{displayTargetType}</span>
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-panel-border bg-background/50 px-3 py-2.5 focus-within:border-cyan/60">
                <Search className="h-4 w-4 shrink-0 text-cyan" />
                <input
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && startScan()}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  placeholder={workspace.searchPlaceholder}
                />
              </div>
              <button data-tour="run-scan" onClick={() => void startScan()} disabled={running || checkingAmbiguity}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60 ring-glow">
                <Play className="h-4 w-4" />
                {checkingAmbiguity ? "Separating identities…" : running ? "Collecting…" : "Run investigation"}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Type a name, email, phone, domain, username or wallet — then press Enter. Add details below if the name is common.
            </p>

            {/* Secondary tools */}
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              <button
                type="button"
                onClick={() => setShowRefine((v) => !v)}
                title="Add optional details (date of birth, city, employer) so look-alikes get filtered out"
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs hover:bg-panel ${refineActive ? "border-cyan/60 bg-cyan/10 text-cyan" : "border-panel-border bg-panel/40"}`}>
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Add details {refineActive ? `· ${Object.values(refine).filter((v) => v.trim()).length}` : ""}
              </button>
              <button
                onClick={() => navigate({ to: "/sources" })}
                title="Choose which data sources are used"
                className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel">
                <Filter className="h-3.5 w-3.5" /> Sources
              </button>
              <button
                onClick={shareScan}
                title="Copy a link to this investigation"
                className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel">
                <Share2 className="h-3.5 w-3.5" /> Share
              </button>
              <button data-tour="export"
                onClick={exportDashboardDossier}
                title="Download the full report as a PDF"
                className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel">
                <Download className="h-3.5 w-3.5" /> Export PDF
              </button>
            </div>
          </div>
          {workspace.playbooks.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">Quick starts</span>
              {workspace.playbooks.map((pb) => (
                <button key={pb.id} onClick={() => applyPlaybook(pb.id)} title={pb.hint} disabled={running}
                  className="rounded-full border border-panel-border bg-background/40 px-2.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-50"
                  style={{ borderColor: `color-mix(in oklab, ${workspace.accent} 35%, transparent)` }}>
                  {pb.label}
                </button>
              ))}
            </div>
          )}
          {showRefine && (
            <div className="mt-3 rounded-md border border-panel-border bg-background/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-medium text-cyan">Extra details (optional)</div>
                <button
                  type="button"
                  onClick={() => setRefine(emptyRefine)}
                  className="text-[11px] text-muted-foreground hover:text-foreground">
                  Clear all
                </button>
              </div>
              <p className="mb-3 text-[11px] text-muted-foreground">
                Provide any extra details you know about the target. We use these to filter out look-alikes and to bias news / search queries toward the right person.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  ["dob", "Date of birth / year", "1986 or 1986-04-12"],
                  ["location", "Lives in (city, country)", "Helsinki, Finland"],
                  ["address", "Address", "Mannerheimintie 4"],
                  ["employer", "Employer / affiliation", "Acme Oy"],
                  ["email", "Known email", "name@example.com"],
                  ["phone", "Known phone", "+358 40 123 4567"],
                  ["aliases", "Aliases / handles (comma)", "j.doe, jdoe88"],
                  ["notes", "Other identifying notes", "Spoke at DEFCON 2022"],
                ].map(([key, label, placeholder]) => (
                  <label key={key} className="flex flex-col gap-1">
                    <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
                    <input
                      value={(refine as Record<string, string>)[key]}
                      onChange={(e) => setRefine((prev) => ({ ...prev, [key]: e.target.value }))}
                      placeholder={placeholder}
                      className="rounded-md border border-panel-border bg-background/60 px-2 py-1.5 text-xs outline-none focus:border-cyan/60"
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
          {/* Live progress (isolated so its animation never re-renders the console) */}
          <ScanProgress running={running} sources={scanResult?.sourceCount ?? 0} />

          {scanError && (
            <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
              The search stopped early: {scanError}
            </div>
          )}
          {scanResult && !scanError && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-success/30 bg-success/10 px-3 py-2 text-xs text-success">
              <span>
                Done — {scanResult.sourceCount} sources checked for {scanResult.target}. Saved to your vault.
              </span>
              <Link
                to="/vault/$scanId"
                params={{ scanId: scanResult.id }}
                className="ml-auto rounded-md border border-success/40 px-2 py-1 text-[11px] hover:bg-success/10"
              >
                Open case
              </Link>
            </div>
          )}
          {scanResult?.confidenceBreakdown && (
            <div className="mt-3">
              <ConfidencePanel breakdown={scanResult.confidenceBreakdown} />
            </div>
          )}
          {breachView && (
            <div className="mt-3 space-y-3">
              {breachView.breaches.length > 0 && <BreachExposureBanner breaches={breachView.breaches} />}
              <BreachRecordsList breaches={breachView.breaches} status={breachView.hibp} />
            </div>
          )}
          {scanResult && (
            <div className="mt-3 space-y-3">
              <FootprintScorePanel
                collectors={collectors}
                findings={findingsList as any}
                compact
              />
              <SourceReliabilityPanel collectors={collectors} compact />
              <ContradictionPanel scanRunId={scanResult.id} collectors={collectors} />
            </div>

          )}
        </div>

        {clarifyPrompt && (
          <div className="col-span-12 rounded-xl border border-amber/40 bg-amber/10 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber">
                More detail needed · ambiguity {clarifyPrompt.score}/100
              </div>
              <button
                type="button"
                onClick={() => setClarifyPrompt(null)}
                className="font-mono text-[10px] text-muted-foreground hover:text-foreground">
                DISMISS
              </button>
            </div>
            <p className="mt-2 text-sm">
              “{clarifyPrompt.target}” is likely to match many different people or entities. Adding one or two details
              below makes the identity resolution — and every score derived from it — meaningful.
            </p>
            {clarifyPrompt.reasons.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                {clarifyPrompt.reasons.map((r) => (<li key={r}>• {r}</li>))}
              </ul>
            )}
            {clarifyPrompt.clarifiers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {clarifyPrompt.clarifiers.map((c) => (
                  <span key={c} className="rounded-full border border-amber/40 px-2 py-0.5 font-mono text-[10px] text-amber">{c}</span>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setShowRefine(true); setClarifyPrompt(null); }}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground">
                Add narrowing details
              </button>
              <button
                type="button"
                disabled={running || checkingAmbiguity}
                onClick={() => { setClarifyPrompt(null); void startScan({ skipClarification: true }); }}
                className="rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-sm hover:bg-panel disabled:opacity-60">
                Search anyway (results may mix identities)
              </button>
            </div>
          </div>
        )}

        {/* Subject panel */}
        {ambiguityReport && (
          <DisambiguationPanel
            report={ambiguityReport}
            busy={running || checkingAmbiguity}
            onDismiss={() => setAmbiguityReport(null)}
            onSearchAll={() => {
              const report = ambiguityReport;
              setAmbiguityReport(null);
              void startScan({
                skipDisambiguation: true,
                ambiguity: { score: report.ambiguityScore, candidateCount: report.candidates.length },
              });
            }}
            onSelect={(candidate: IdentityCandidate) => {
              const report = ambiguityReport;
              const merged = {
                ...refine,
                location: refine.location || candidate.suggestedRefine.location || "",
                employer: refine.employer || candidate.suggestedRefine.employer || "",
                notes: refine.notes || candidate.suggestedRefine.notes || "",
                aliases: refine.aliases || candidate.aliases.slice(0, 4).join(", "),
              };
              setRefine(merged);
              setShowRefine(true);
              setAmbiguityReport(null);
              void startScan({
                skipDisambiguation: true,
                refineOverride: merged,
                ambiguity: {
                  score: Math.max(5, Math.min(60, 100 - candidate.confidence)),
                  candidateCount: 1,
                  selectedCandidate: candidate.label,
                },
                ...(report ? {} : {}),
              });
            }}
          />
        )}
        <div className="col-span-12 lg:col-span-8 glass rounded-xl p-5">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="relative">
              <div className="grid h-20 w-20 place-items-center rounded-2xl bg-gradient-to-br from-cyan/30 to-[oklch(0.55_0.20_240)]/30 ring-1 ring-cyan/40">
                <span className="font-display text-2xl font-semibold">{initials(primarySubject?.label ?? scanResult?.target ?? "—")}</span>
              </div>
              {scanResult && (
                <div className="absolute -right-1 -bottom-1 h-5 w-5 rounded-full bg-success ring-2 ring-background grid place-items-center">
                  <ShieldCheck className="h-3 w-3 text-background" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] tracking-widest text-cyan">
                {scanResult ? `PRIMARY SUBJECT · CONFIDENCE ${confidence}%` : "NO ACTIVE SCAN"}
              </div>
              <h1 className="mt-1 font-display text-3xl">
                {primarySubject?.label ?? scanResult?.target ?? "Run an investigation to begin"}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {scanResult?.target ?? "—"}</span>
                {scanResult && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Backend run {scanResult.id.slice(0, 8)}</span>}
                {scanResult && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Public OSINT backend</span>}
              </div>
            </div>
            <div className="flex gap-2">
              {trustScore === null ? (
                <NoScoreTile label={workspace.scoreLabel} />
              ) : (
                <RiskGauge score={Math.max(0, Math.min(100, trustScore))} label={workspace.scoreLabel} tone="cyan" />
              )}
              {confidence === null ? <NoScoreTile label="Confidence" /> : <RiskGauge score={confidence} label="Confidence" tone="cyan" />}
            </div>
          </div>

          {/* Tabs */}
          <div data-tour="tabs" className="mt-5 flex flex-wrap gap-1 border-b border-panel-border">
            {workspace.tabs.map((k) => (
              <button key={k} onClick={() => setTab(k)}
                className={`relative px-3 py-2 text-sm transition-colors ${tab === k ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {TAB_LABELS[k]}
                {tab === k && <span className="absolute -bottom-px left-0 right-0 h-0.5" style={{ background: workspace.accent }} />}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="mt-4">
          {tab === "profile"  && <SubjectProfile result={scanResult} />}
          {tab === "overview" && (
            <div className="space-y-4">
              {deepResults && (
                <DeepResults
                  data={deepResults}
                  onPivot={(t) => { setTarget(t); setTimeout(() => startScan(), 0); }}
                />
              )}
              <EvidenceSections findings={findingsList} />
              <Overview onSelect={setSelected} result={scanResult} compact={!!deepResults} />
            </div>
          )}
            {tab === "business" && (
              <BusinessIntelPanel
                company={scanResult?.target || target}
                onPivot={(t) => { setTarget(t); setTab("profile"); }}
              />
            )}
            {tab === "graph"    && <div className="h-[520px]"><NetworkGraph onSelect={setSelected} entities={scanResult?.entities ?? []} /></div>}
            {tab === "timeline" && <Timeline result={scanResult} />}
            {tab === "findings" && <Findings result={scanResult} />}
            {tab === "infra"    && <Infra result={scanResult} target={target} />}
            {tab === "report"   && <Dossier result={scanResult} target={target} />}
          </div>
        </div>

        {/* Right rail: AI Analyst */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
          {scanResult ? (
            <ScanAiChat
              key={scanResult.id}
              scanRunId={scanResult.id}
              target={scanResult.target}
              onBroaden={() => {
                if (running) return;
                setRefine(emptyRefine);
                setShowRefine(false);
                setTarget(scanResult.target);
                toast.message("Broadening search…", { description: "Re-running collectors with all refine filters cleared." });
                setTimeout(() => startScan(), 0);
              }}
            />
          ) : (
            <AiPanel scanResult={null} />
          )}
          <SourceTelemetry />
          <div data-tour="entity-card"><SelectedEntityCard
            e={selected}
            onPivot={(ent) => {
              setTarget(ent.label);
              setSelected(null);
              setTimeout(() => startScan(), 0);
            }}
            onPin={(ent) => {
              if (!activeCaseId) { alert("Open or run a case first."); return; }
              addEvidence(activeCaseId, {
                kind: ent.type === "wallet" ? "ioc" : ent.type === "domain" || ent.type === "ip" ? "ioc" : "doc",
                label: ent.label,
                source: `entity:${ent.type}`,
                content: `${ent.label} (${ent.type}) · confidence ${ent.confidence}% · risk ${ent.risk}${ent.sub ? `\n${ent.sub}` : ""}`,
                tags: ["pinned-from-graph", ent.type],
              });
              alert(`Pinned ${ent.label} to case ${activeCaseId} evidence.`);
            }}
            onOpenDossier={() => setTab("report")}
          /></div>
        </div>
      </div>
      <OnboardingTour steps={DASHBOARD_TOUR} />
    </AppLayout>
  );
}

const DASHBOARD_TOUR: TourStep[] = [
  {
    title: "Welcome to CARIO INTEL",
    body: "This quick tour shows how to run a scan, pivot on an entity, and export an integrity-stamped dossier. You can replay it anytime from the Help button in the top bar.",
  },
  {
    selector: "[data-tour=\"command-bar\"]",
    title: "1. Enter a target",
    body: "Type any target — email, domain, IP, wallet, username, phone, or organisation. The platform auto-detects the type.",
  },
  {
    selector: "[data-tour=\"run-scan\"]",
    title: "2. Run the investigation",
    body: "Click Run investigation (or press Enter). 60+ public collectors fan out in parallel and a case is created automatically.",
  },
  {
    selector: "[data-tour=\"tabs\"]",
    title: "3. Explore the results",
    body: "Switch between Overview, Network graph, Timeline, Findings, Infrastructure, and the printable Dossier.",
  },
  {
    selector: "[data-tour=\"entity-card\"]",
    title: "4. Select an entity",
    body: "Click any node in the graph or any related entity. From here you can Pivot to scan it, Pin it to evidence, or jump to the dossier.",
  },
  {
    selector: "[data-tour=\"export\"]",
    title: "5. Export a signed PDF",
    body: "Export the full dossier — findings, entities, timeline, collector telemetry — as a chain-of-custody PDF.",
  },
];

/**
 * Self-contained progress indicator. It keeps its own ticking state so the
 * animation never re-renders the (very large) console tree above it.
 */
function ScanProgress({ running, sources }: { running: boolean; sources: number }) {
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    if (!running) { setProgress(100); return; }
    setProgress(6);
    // Deterministic easing toward 92%. A random walk would imply we know how
    // much work is left; we don't, so at least don't fake different numbers
    // on every identical run.
    let ticks = 0;
    const id = setInterval(() => {
      ticks += 1;
      setProgress(Math.min(6 + 86 * (1 - Math.exp(-ticks / 12)), 92));
    }, 600);
    return () => clearInterval(id);
  }, [running]);
  return (
    <div className="mt-3 flex items-center gap-3">
      <div className="text-[11px] text-muted-foreground">{running ? "Collecting evidence" : "Progress"}</div>
      <div className={`relative h-1.5 flex-1 overflow-hidden rounded-full bg-panel ${running ? "sweep" : ""}`}>
        <div className="h-full bg-gradient-to-r from-cyan to-[oklch(0.55_0.20_240)] transition-all"
          style={{ width: `${progress}%` }} />
      </div>
      <div className="w-10 text-right font-mono text-[10px] text-muted-foreground">{Math.round(progress)}%</div>
      <div className="hidden gap-3 font-mono text-[10px] text-muted-foreground md:flex">
        <span>· {sources} sources</span>
      </div>
    </div>
  );
}

/** Shown instead of a gauge when the evidence does not support any number. */
function NoScoreTile({ label }: { label: string }) {
  return (
    <div className="grid h-[74px] w-[74px] place-items-center rounded-full border border-dashed border-panel-border text-center">
      <div>
        <div className="font-mono text-[10px] uppercase text-muted-foreground">No</div>
        <div className="font-mono text-[10px] uppercase text-muted-foreground">score</div>
      </div>
      <span className="sr-only">{label}: not calculable — insufficient evidence</span>
    </div>
  );
}

function RiskGauge({ score, label, tone }: { score: number; label: string; tone: "amber" | "cyan" }) {

  const color = tone === "amber" ? "var(--amber)" : "var(--cyan)";
  const C = 2 * Math.PI * 28;
  const off = C - (score / 100) * C;
  return (
    <div className="relative grid h-24 w-24 place-items-center">
      <svg viewBox="0 0 64 64" className="absolute inset-0 -rotate-90">
        <circle cx="32" cy="32" r="28" fill="none" stroke="var(--panel-border)" strokeWidth="4" />
        <circle cx="32" cy="32" r="28" fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={C} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <div className="text-center">
        <div className="font-display text-xl font-semibold">{score}</div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function Overview({ onSelect, result, compact = false }: { onSelect: (e: Entity) => void; result: BackendScanResult | null; compact?: boolean }) {
  if (!result) {
    return (
      <div className="glass rounded-lg p-6 text-center text-sm text-muted-foreground">
        Enter a target above and run an investigation. All findings shown here come from your real scans.
      </div>
    );
  }
  const exec = [
    `Backend run ${result.id} completed against ${result.target} using ${result.sourceCount} lawful public collectors.`,
    `Target classified as ${result.targetType}; composite risk scored ${result.riskScore}/100 with ${result.confidence}% confidence.`,
    `${result.findings.length} findings and ${result.entities.length} entities were generated and persisted to the case evidence ledger.`,
    "Methodology limited to legal OSINT: public DNS, RDAP, certificate transparency, public indexes, and open watchlists.",
  ];
  const entities = result.entities;
  const indicators = result.findings.slice(0, 4).map((f) => ({
    i: f.severity === "critical" ? Wallet : f.severity === "high" ? AlertTriangle : f.severity === "medium" ? Globe : ShieldCheck,
    l: f.category,
    v: f.title,
    t: f.severity,
  }));
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {!compact && <div className="glass rounded-lg p-4">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
          <Sparkles className="h-3 w-3" /> AI executive summary
        </div>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed">
          {exec.map((e, i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan" />
              <span>{e}</span>
            </li>
          ))}
        </ul>
      </div>}

      {!compact && <div className="glass rounded-lg p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Threat indicators</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {indicators.map(({ i: I, l, v, t }) => (
            <div key={l} className="rounded-md border border-panel-border bg-background/40 p-3">
              <div className="flex items-center justify-between">
                <I className="h-4 w-4 text-muted-foreground" />
                <SeverityBadge s={t as "low"} />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">{l}</div>
              <div className="font-display text-base">{v}</div>
            </div>
          ))}
        </div>
      </div>}

      <div className="glass rounded-lg p-4 md:col-span-2">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Associated entities · {entities.length}</div>
          <button className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            Open graph <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {entities.slice(1, 10).map((e) => (
            <button key={e.id} onClick={() => onSelect(e)}
              className="group flex items-center gap-3 rounded-md border border-panel-border bg-background/40 p-2.5 text-left hover:bg-panel/60 transition-colors">
              <EntityIcon t={e.type} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{e.label}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{e.type.toUpperCase()} · {e.confidence}%</div>
              </div>
              <div className={`font-mono text-[10px] ${e.risk > 70 ? "text-danger" : e.risk > 40 ? "text-amber" : "text-success"}`}>
                R{e.risk}
              </div>
              <ChevronRight className="h-3 w-3 text-muted-foreground group-hover:text-foreground" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function EntityIcon({ t }: { t: Entity["type"] }) {
  const M: Record<Entity["type"], typeof Mail> = {
    person: Brain, email: Mail, domain: Globe, ip: Activity, wallet: Wallet,
    org: Building2, username: Sparkles, phone: Activity, device: Cpu, location: MapPin,
  };
  const I = M[t] || Sparkles;
  return (
    <div className="grid h-9 w-9 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
      <I className="h-4 w-4 text-cyan" />
    </div>
  );
}

function Timeline({ result }: { result: BackendScanResult | null }) {
  const events = result?.timeline ?? [];
  if (!events.length) {
    return <div className="rounded-md border border-panel-border bg-background/40 p-6 text-center text-sm text-muted-foreground">No timeline yet. Run a scan to populate this view.</div>;
  }
  return (
    <div className="relative pl-6">
      <div className="absolute left-2 top-2 bottom-2 w-px bg-panel-border" />
      <div className="space-y-4">
        {events.map((t) => (
          <div key={t.id} className="relative">
            <div className="absolute -left-[18px] top-1.5 h-3 w-3 rounded-full bg-background ring-2 ring-cyan pulse-dot" />
            <div className="glass rounded-md p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-mono text-[11px] text-cyan">{t.ts}</div>
                <SeverityBadge s={t.severity} />
              </div>
              <div className="mt-1 text-sm">{t.title}</div>
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">SOURCE · {t.source}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Findings({ result }: { result: BackendScanResult | null }) {
  return <EvidenceSections findings={result?.findings ?? []} />;
}

function Infra({ result, target }: { result: BackendScanResult | null; target: string }) {
  if (!result) {
    return <div className="rounded-md border border-panel-border bg-background/40 p-6 text-center text-sm text-muted-foreground">No collector results yet. Run a scan against {target || "a target"}.</div>;
  }
  const collectors = (result.raw.collectors ?? []) as Array<{ source: string; status: string; summary: string; latencyMs: number }>;
  const fusion = (result.raw as any)?.fusion ?? null;
  return (
    <div className="space-y-3">
      <SourceMeshPanel collectors={collectors} fusion={fusion} />
      {fusion?.claims?.length ? (
        <div className="glass rounded-md overflow-hidden">
          <div className="border-b border-panel-border px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Fused claims · {target}
          </div>
          <div className="divide-y divide-panel-border">
            {fusion.claims.slice(0, 10).map((c: any) => (
              <div key={c.id} className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span className="text-cyan">{c.kind}</span>
                  <span>weight {c.weight}</span>
                  <span>· {c.corroboration} independent source{c.corroboration === 1 ? "" : "s"}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{c.claim}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}


function Dossier({ result, target }: { result: BackendScanResult | null; target: string }) {
  const { state } = useInvestigations();
  const activeCaseId = state.cases[0]?.id ?? null;
  if (!result) {
    return <div className="glass rounded-md p-6 text-center text-sm text-muted-foreground">No dossier yet. Run a scan to generate one from real collector results.</div>;
  }
  const metrics = [
    ["Identity confidence", `${result.confidence}%`],
    ["Composite risk", `${result.riskScore} / 100`],
    ["Sources cited", String(result.sourceCount)],
    ["Entities resolved", String(result.entities.length)],
    ["Findings generated", String(result.findings.length)],
    ["Backend run", result.id.slice(0, 8)],
  ];
  const sections = [
    ["1. Executive Summary", `Backend scan ${result.id} executed for ${result.target}. Target type ${result.targetType}; risk ${result.riskScore}/100; confidence ${result.confidence}%.`],
    ["2. Collector Results", result.raw.collectors.slice(0, 5).map((c) => `${c.source}: ${c.summary}`).join(" ")],
    ["3. Risk Assessment", result.findings.slice(0, 4).map((f) => `${f.severity.toUpperCase()} — ${f.title}`).join(" ")],
    ["4. Legal OSINT Scope", result.raw.legalScope],
  ];
  return (
    <div id="dashboard-dossier-export" className="glass rounded-md p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">DOSSIER · {activeCaseId ?? "BACKEND"}</div>
          <div className="font-display text-2xl mt-1">Subject Intelligence Report · {result.target}</div>
        </div>
        <button
          onClick={async () => {
            const ok = await exportScanDossier({
              result,
              filename: `${result.id}-signed-dossier.pdf`,
            });
            if (!ok) alert("PDF export failed — see console for details.");
          }}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-glow">
          <Download className="h-4 w-4" /> Export signed PDF
        </button>
      </div>
      <div className="divider-soft my-5" />
      <div className="grid gap-4 md:grid-cols-3">
        {metrics.map(([l, v]) => (
          <div key={l} className="rounded-md border border-panel-border bg-background/40 p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
            <div className="font-display text-xl mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 space-y-3">
        {sections.map(([t, b]) => (
          <div key={t}>
            <div className="text-sm font-semibold">{t}</div>
            <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{b}</div>
          </div>
        ))}
      </div>
      <div className="divider-soft my-5" />
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Generated by CARIO INTEL · {result.completedAt} · Backend persistence verified · Chain-of-custody preserved
      </div>
    </div>
  );
}

function AiPanel({ scanResult }: { scanResult: BackendScanResult | null }) {
  const ask = useServerFn(askAnalyst);
  const [msgs, setMsgs] = useState<{ role: "user" | "assistant"; content: string }[]>([
    { role: "assistant", content: "Ready when you are. Run a scan and I'll correlate findings across collectors with live web search." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...msgs, { role: "user" as const, content: text }];
    setMsgs(next);
    setInput("");
    setErr(null);
    setBusy(true);
    try {
      const ctxMsgs = scanResult
        ? [{
            role: "assistant" as const,
            content: `Active scan context — target: ${scanResult.target} (${scanResult.targetType}); run ${scanResult.id}; risk ${scanResult.riskScore}/100; confidence ${scanResult.confidence}%; ${scanResult.sourceCount} collectors; ${scanResult.findings.length} findings.`,
          }, ...next]
        : next;
      const { reply } = await ask({
        data: {
          messages: ctxMsgs.map((m) => ({ role: m.role, content: m.content })),
          environment: getPaddleEnvironment(),
        },
      });
      setMsgs((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "AI request failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="glass rounded-xl p-3 flex flex-col h-[440px]">
      <div className="flex items-center gap-2 px-1 pb-2 border-b border-panel-border">
        <div className="grid h-7 w-7 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
          <Bot className="h-4 w-4 text-cyan" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold">AI Analyst</div>
          <div className="font-mono text-[10px] text-muted-foreground">CARIO-AGENT v3.4</div>
        </div>
        <div className="ml-auto font-mono text-[10px] text-success">● ONLINE</div>
      </div>
      <div className="flex-1 overflow-y-auto py-2 space-y-2">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
            <div className={`max-w-[88%] whitespace-pre-wrap rounded-md px-3 py-2 text-sm leading-relaxed ${
              m.role === "user" ? "bg-cyan/15 ring-1 ring-cyan/30" : "bg-background/60 border border-panel-border"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && <div className="flex"><div className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm text-muted-foreground animate-pulse">CARIO-AGENT analyzing…</div></div>}
        {err && <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}
      </div>
      <div className="border-t border-panel-border pt-2 space-y-1.5">
        <div className="flex flex-wrap gap-1">
          {["Expand on-chain trace", "Find press mentions", "Compare to peers", "Generate hypothesis"].map((s) => (
            <button key={s} onClick={() => setInput(s)}
              className="rounded-full border border-panel-border bg-background/40 px-2.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:text-foreground hover:border-cyan/40">
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 rounded-md border border-panel-border bg-background/40 px-2 py-1.5">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask the analyst…"
            disabled={busy}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:opacity-60" />
          <button onClick={send} disabled={busy} className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SourceTelemetry() {
  return (
    <div className="glass rounded-xl p-3">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Live source telemetry</div>
        <div className="font-mono text-[10px] text-muted-foreground">live</div>
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        Source status appears here while a scan is running.
      </div>
    </div>
  );
}

function SelectedEntityCard({
  e, onPivot, onPin, onOpenDossier,
}: {
  e: Entity | null;
  onPivot: (ent: Entity) => void;
  onPin: (ent: Entity) => void;
  onOpenDossier: () => void;
}) {
  if (!e) {
    return (
      <div className="glass rounded-xl p-4 text-center text-sm text-muted-foreground">
        <Sparkles className="mx-auto h-4 w-4 text-cyan" />
        <div className="mt-2">Click any node in the network or any associated entity to inspect.</div>
      </div>
    );
  }
  return (
    <div className="glass rounded-xl p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Selected node</div>
      <div className="mt-1 flex items-center gap-3">
        <EntityIcon t={e.type} />
        <div className="min-w-0">
          <div className="truncate font-display text-base">{e.label}</div>
          <div className="font-mono text-[10px] text-muted-foreground">{e.type.toUpperCase()} · conf {e.confidence}% · risk {e.risk}</div>
        </div>
      </div>
      {e.sub && <div className="mt-2 text-sm text-muted-foreground">{e.sub}</div>}
      <div className="mt-3 flex gap-1.5 flex-wrap">
        <button onClick={() => onPivot(e)} className="rounded-md border border-panel-border bg-background/40 px-2 py-1 text-xs hover:bg-panel">Pivot</button>
        <button onClick={() => onPin(e)} className="rounded-md border border-panel-border bg-background/40 px-2 py-1 text-xs hover:bg-panel">Pin to evidence</button>
        <button onClick={onOpenDossier} className="rounded-md border border-panel-border bg-background/40 px-2 py-1 text-xs hover:bg-panel">Open dossier</button>
      </div>
    </div>
  );
}
